import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import getPolicyName from 'auth/getPolicyName';
import {Collaborator, COLLABORATOR_SHAPE, isActorCollaborator} from 'auth/PermissionConstants';
import {
  getCollaboratorAdminPolicyId,
  getCollaboratorAdminPolicyKey,
  getCollaboratorMemberPolicyId,
  getCollaboratorMemberPolicyKey,
  getCollaboratorPolicyId,
  getCollaboratorPolicyKey,
} from 'auth/PermissionUtil';
import {Policy, POLICY_SHAPE} from 'auth/PolicyConstants';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import EntityStore from 'stores/EntityStore';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';
import {ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import * as modals from 'util/Modals';

interface Props<T extends Policy> {
  collaborator: Collaborator;
  isReadOnly: boolean;
  onChangeCollaboratorPolicy: (collaboratorId: string, policyData: {policyKey: string | null}) => void;
  onLeave?: (collaboratorId: string) => void;
  onRemoveCollaborator: (collaboratorId: string) => void;
  policies: Array<T>;
  renderExtraColumns?: ((collaboratorId: string) => JSX.Element) | null;
}

interface PolicyDropdownProps<T extends Policy> {
  allowNone: boolean;
  policies: Array<T>;
  policyKey: string;
  isReadOnly: boolean;
  onChangePolicy: (policyData: {[policyKey: string]: string | null}) => void;
  value: string | null;
}

export const NONE = 'NONE';

export class PolicyDropdown<T extends Policy> extends React.Component<PolicyDropdownProps<T>> {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleChangePolicy = (policyId: string) => {
    if (policyId === NONE) {
      this.props.onChangePolicy({[this.props.policyKey]: null});
      return;
    }
    this.props.onChangePolicy({[this.props.policyKey]: policyId});
  };
  render(): JSX.Element {
    const currentPolicy = this.props.value
      ? this.props.policies.find((policy) => policy.id === this.props.value)
      : null;
    const currentPolicyName = currentPolicy ? getPolicyName(currentPolicy) : NONE;
    return (
      <SelectDropdown
        disabled={this.props.isReadOnly}
        displayName={currentPolicyName}
        menu={
          <Menu
            height={Menu.HEIGHTS.MEDIUM}
            width={Menu.WIDTHS.FULL}
            scrollable={true}
            value={currentPolicyName}
          >
            {this.props.allowNone && (
              <Option value={NONE} onSelect={this.handleChangePolicy}>
                {NONE}
              </Option>
            )}
            {this.props.policies.map((policy: T) => (
              <Option key={policy.id} value={policy.id} onSelect={this.handleChangePolicy}>
                {getPolicyName(policy)}
              </Option>
            ))}
          </Menu>
        }
        dataTestAttrs={{component: 'SelectDropdown', element: 'input', key: 'policy'}}
      />
    );
  }
}

export default class CollaboratorRowView<T extends Policy> extends React.Component<Props<T>> {
  static propTypes = {
    collaborator: COLLABORATOR_SHAPE,
    isReadOnly: PropTypes.bool.isRequired,
    onChangeCollaboratorPolicy: PropTypes.func.isRequired,
    onLeave: PropTypes.func,
    onRemoveCollaborator: PropTypes.func.isRequired,
    policies: PropTypes.arrayOf(POLICY_SHAPE.isRequired).isRequired,
    renderExtraColumns: PropTypes.func,
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleChangePolicy = (policyData: {policyKey: string | null}) => {
    this.props.onChangeCollaboratorPolicy(this.props.collaborator.id, policyData);
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleRemoveCollaborator = () => {
    this.props.onRemoveCollaborator(this.props.collaborator.id);
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleLeave = async () => {
    const confirm = await Promise.resolve(
      modals.confirm(
        'Leave',
        `Are you sure you want to remove ${getPartyDisplayString(
          this.props.collaborator
        )} as a collaborator? This action cannot be undone.`,
        ButtonStyle.NEGATIVE,
        {
          promiseCompatible: true,
          enableRedesign: true,
        }
      )
    );
    if (!confirm) {
      return;
    }
    this.props.onLeave!(this.props.collaborator.id);
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _canLeave = () => {
    const currentUser = EntityStore.currentUser();
    if (!currentUser) {
      return false;
    }
    return (
      this.props.collaborator.id === currentUser.id ||
      // Show leave option if org/team member in case they expect to be able to leave ownable, but backend
      // only permits if admin.
      currentUser.organizationIds.includes(this.props.collaborator.id) ||
      currentUser.teamIds.includes(this.props.collaborator.id)
    );
  };

  render(): Array<JSX.Element | boolean> {
    const sharedPolicyDropdownProps = {
      isReadOnly: this.props.isReadOnly,
      policies: this.props.policies,
      onChangePolicy: this._handleChangePolicy,
    };
    return [
      <tr key="row" {...getTestProps({component: 'CollaboratorRowView', element: 'root'})}>
        <td className={css(styles.partyCell)}>
          <strong>{getPartyDisplayString(this.props.collaborator)}</strong>
        </td>
        <td>
          {isActorCollaborator(this.props.collaborator) ? (
            <PolicyDropdown
              {...sharedPolicyDropdownProps}
              allowNone={false}
              policyKey={getCollaboratorPolicyKey(this.props.collaborator)}
              value={getCollaboratorPolicyId(this.props.collaborator)}
            />
          ) : (
            <PolicyDropdown
              {...sharedPolicyDropdownProps}
              allowNone={true}
              policyKey={getCollaboratorMemberPolicyKey(this.props.collaborator)}
              value={getCollaboratorMemberPolicyId(this.props.collaborator)}
            />
          )}
        </td>
        {this.props.renderExtraColumns && this.props.renderExtraColumns(this.props.collaborator.id)}
        {!(this.props.isReadOnly || this.props.collaborator.owner) && (
          <td>
            <BenchlingIcon
              icon="trash-can"
              className="removeCollaborator u-faded"
              onClick={this._handleRemoveCollaborator}
              dataTestAttrs={{component: 'CollaboratorRowView', element: 'removeCollaboratorButton'}}
            />
          </td>
        )}
        {this.props.isReadOnly && this.props.onLeave && this._canLeave() ? (
          <td>
            <Tooltip title="Leave">
              <BenchlingIcon
                icon="trash-can"
                className="removeCollaborator u-faded"
                onClick={this._handleLeave}
              />
            </Tooltip>
          </td>
        ) : (
          <td />
        )}
      </tr>,
      !isActorCollaborator(this.props.collaborator) && (
        <tr key="admin-role-row">
          <td className={classNames(css(styles.adminRowCell), css(styles.partyCell))}>
            Admins of {getPartyDisplayString(this.props.collaborator)}
          </td>
          <td className={css(styles.adminRowCell)}>
            <PolicyDropdown
              {...sharedPolicyDropdownProps}
              allowNone={false}
              policyKey={getCollaboratorAdminPolicyKey(this.props.collaborator)}
              // Show the member data policy instead of NONE to help indicate it that admins will receive
              // member policy.
              value={
                getCollaboratorAdminPolicyId(this.props.collaborator)
                  ? getCollaboratorAdminPolicyId(this.props.collaborator)
                  : getCollaboratorMemberPolicyId(this.props.collaborator)
              }
            />
          </td>
        </tr>
      ),
    ];
  }
}

const styles = StyleSheet.create({
  adminRowCell: {
    borderTop: 'none',
  },
  partyCell: {
    overflowWrap: 'break-word',
    wordWrap: 'break-word',
  },
});
